/** National emergency numbers shown on every booking safety panel. */
export const NATIONAL_EMERGENCY = {
  saps: '10111',
  gbvHelpline: '0800428428',
  ambulance: '10177',
} as const;

export interface CampusSecurityLine {
  label: string;
  number: string;
}

export interface CampusSafetyContact {
  institution: string;
  protectionLabel: string;
  /** Primary number used for the main tel: link. */
  protectionServices: string;
  /** Additional campus-specific control rooms. */
  lines?: readonly CampusSecurityLine[];
  tollFree?: string;
  escortInfo?: string;
  safetyApp?: string;
  safetyAppNote?: string;
  offCampusNote?: string;
  /** Official page where numbers were verified. */
  sourceUrl: string;
  /** ISO date when numbers were last checked against the source. */
  lastVerified: string;
  /**
   * No published 24-hour campus security line — UI should show guidance
   * instead of a campus tel: link.
   */
  noDedicatedLine?: boolean;
  noDedicatedLineNote?: string;
}

/**
 * Campus protection contacts for all Makhe-whitelisted institutions.
 * Numbers sourced from each institution's official website or published handbook.
 */
export const CAMPUS_SAFETY_CONTACTS: ReadonlyArray<CampusSafetyContact> = [
  {
    institution: 'Boston City Campus',
    protectionLabel: 'Campus security',
    protectionServices: '',
    noDedicatedLine: true,
    noDedicatedLineNote:
      'Boston does not publish a central 24-hour campus security line. In an emergency call SAPS 10111. During office hours contact your campus directly.',
    offCampusNote: 'Off-campus bookings are outside any campus security coverage.',
    sourceUrl: 'https://www.boston.co.za/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Cape Peninsula University of Technology',
    protectionLabel: 'CPUT Protection Services',
    protectionServices: '0219596301',
    lines: [
      { label: 'Bellville', number: '0219596301' },
      { label: 'Bellville (alt)', number: '0219596550' },
      { label: 'District Six (Cape Town)', number: '0214603122' },
      { label: 'Wellington', number: '0218645551' },
      { label: 'Newlands', number: '0216572004' },
    ],
    escortInfo: '24-hour protection on all campuses — contact your campus control room.',
    offCampusNote: 'Off-campus residences are outside CPUT Protection Services coverage.',
    sourceUrl: 'https://www.cput.ac.za/services/cps/emergency-contacts',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Central University of Technology',
    protectionLabel: 'CUT Protection Services',
    protectionServices: '0800060060',
    lines: [
      { label: 'Bloemfontein hotline', number: '0800060060' },
      { label: 'Bloemfontein', number: '0515073237' },
      { label: 'Welkom hotline', number: '0800060061' },
      { label: 'Welkom', number: '0579103799' },
    ],
    offCampusNote: 'Off-campus student housing is outside CUT security patrol areas.',
    sourceUrl: 'https://www.cut.ac.za/contact-us',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Durban University of Technology',
    protectionLabel: 'DUT Protection Services',
    protectionServices: '0313732181',
    lines: [
      { label: 'Security control room', number: '0313732181' },
      { label: 'Security control room (alt)', number: '0313732182' },
      { label: 'CCTV control room', number: '0313735555' },
    ],
    offCampusNote: 'Off-campus residences are outside DUT security coverage.',
    sourceUrl: 'https://www.dut.ac.za/support_services/protection-services/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Emeris (IIE — Varsity College, MSA, Vega)',
    protectionLabel: 'Campus security',
    protectionServices: '',
    noDedicatedLine: true,
    noDedicatedLineNote:
      'IIE colleges do not publish a single national campus security hotline. In an emergency call SAPS 10111. Contact your campus reception during office hours.',
    offCampusNote: 'Off-campus bookings rely on SAPS — no unified IIE security coverage.',
    sourceUrl: 'https://www.iie.ac.za/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'False Bay TVET College',
    protectionLabel: 'Campus reception',
    protectionServices: '0217870800',
    lines: [
      { label: 'Central office', number: '0217870800' },
      { label: 'Muizenberg', number: '0217888373' },
      { label: 'Fish Hoek', number: '0217820144' },
      { label: 'Khayelitsha', number: '0213605000' },
      { label: 'Mitchells Plain', number: '0213910717' },
      { label: 'Westlake', number: '0217011340' },
    ],
    tollFree: '0800110770',
    noDedicatedLineNote:
      'No dedicated 24-hour security line published — toll-free whistleblower line available 24/7. For emergencies call SAPS 10111.',
    offCampusNote: 'Contact your campus during office hours; after hours use SAPS.',
    sourceUrl: 'https://falsebaycollege.co.za/contact-us/contact-details/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'George Whitefield College',
    protectionLabel: 'Campus security',
    protectionServices: '',
    noDedicatedLine: true,
    noDedicatedLineNote:
      'GWC does not publish a campus security hotline. In an emergency call SAPS 10111.',
    sourceUrl: 'https://www.gwc.ac.za/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'IIE Rosebank College',
    protectionLabel: 'Campus security',
    protectionServices: '',
    noDedicatedLine: true,
    noDedicatedLineNote:
      'Rosebank College does not publish a central 24-hour security line. In an emergency call SAPS 10111. Contact your campus during office hours.',
    sourceUrl: 'https://www.rosebankcollege.co.za/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'MANCOSA',
    protectionLabel: 'Campus security',
    protectionServices: '',
    noDedicatedLine: true,
    noDedicatedLineNote:
      'MANCOSA is primarily distance learning with no published campus security hotline. In an emergency call SAPS 10111.',
    sourceUrl: 'https://www.mancosa.co.za/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Mangosuthu University of Technology',
    protectionLabel: 'MUT Protection Services',
    protectionServices: '0318199486',
    escortInfo: 'Report suspicious activity to the Protection Services control room.',
    offCampusNote: 'Off-campus housing in Umlazi is outside MUT security patrol areas.',
    sourceUrl: 'https://www.mut.ac.za/protection-services-invites-staff-and-students-to-work-with-it-to-ensure-everyones-safety/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Milpark Education',
    protectionLabel: 'Campus security',
    protectionServices: '',
    noDedicatedLine: true,
    noDedicatedLineNote:
      'Milpark does not publish a campus security hotline. In an emergency call SAPS 10111.',
    sourceUrl: 'https://www.milpark.ac.za/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Nelson Mandela University',
    protectionLabel: 'NMU Protection Services',
    protectionServices: '0415042009',
    lines: [
      { label: 'General emergency (Gqeberha)', number: '0415042009' },
      { label: 'Gqeberha protection', number: '0415049111' },
      { label: 'North campus', number: '0415043483' },
      { label: 'South campus', number: '0415042482' },
      { label: 'George campus', number: '0448015183' },
    ],
    offCampusNote: 'Off-campus residences are outside NMU Protection Services coverage.',
    sourceUrl: 'https://protection.mandela.ac.za/Service-Point-Contact-Details',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'North-West University',
    protectionLabel: 'NWU Protection Services',
    protectionServices: '0182992211',
    lines: [
      { label: 'Potchefstroom', number: '0182992211' },
      { label: 'Potchefstroom (alt)', number: '0182992215' },
      { label: 'Mahikeng', number: '0183892167' },
      { label: 'Mahikeng (alt)', number: '0183892277' },
      { label: 'Vanderbijlpark', number: '0169103339' },
    ],
    escortInfo: '24-hour protection on all three campuses.',
    offCampusNote: 'Off-campus student housing is outside NWU security patrol areas.',
    sourceUrl: 'https://services.nwu.ac.za/protection-services/emergency-contact-details-potchefstroom-campus',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Rhodes University',
    protectionLabel: 'Rhodes Campus Protection',
    protectionServices: '0466038999',
    lines: [{ label: 'CPU emergency (alt)', number: '0466038146' }],
    escortInfo: '24-hour CPU emergency line — save on your phone.',
    offCampusNote: 'Private off-campus housing is outside CPU patrol areas.',
    sourceUrl: 'https://www.ru.ac.za/campusprotection/importantcontacts/emergencynumbers/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Sol Plaatje University',
    protectionLabel: 'SPU Campus Security',
    protectionServices: '0534910911',
    lines: [{ label: 'Client service centre', number: '0534910365' }],
    escortInfo: '24-hour escort service on campus — call the control room.',
    offCampusNote: 'Off-campus residences use Pro-Guard security on approved routes.',
    sourceUrl: 'https://www.spu.ac.za/index.php/campus-security/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'South African Theological Seminary',
    protectionLabel: 'Campus security',
    protectionServices: '',
    noDedicatedLine: true,
    noDedicatedLineNote:
      'SATS does not publish a campus security hotline. In an emergency call SAPS 10111.',
    sourceUrl: 'https://www.sats.edu.za/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Stellenbosch University',
    protectionLabel: 'SU Campus Security',
    protectionServices: '0218082333',
    tollFree: '0800025827',
    lines: [{ label: 'Tygerberg campus emergency', number: '0219389507' }],
    escortInfo: 'Evening walk-with escorts (18:00–06:00) — WhatsApp 0828082333 (Stellenbosch).',
    safetyApp: 'Buzzer',
    safetyAppNote: 'Buzzer SOS is geofenced to Stellenbosch campus only.',
    offCampusNote: 'Private off-campus housing is outside Buzzer geofence.',
    sourceUrl: 'https://www.su.ac.za/en/students/campus-life/safety',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Tshwane University of Technology',
    protectionLabel: 'TUT Campus Protection',
    protectionServices: '0123825533',
    lines: [{ label: 'Contact centre (ask for CPS)', number: '0861102421' }],
    offCampusNote:
      'TUT does not publish a dedicated CPS hotline — use the main line and ask for Campus Protection Services. Off-campus residences are outside TUT security coverage.',
    sourceUrl: 'https://www.tut.ac.za/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of Cape Town',
    protectionLabel: 'UCT Campus Protection Services',
    protectionServices: '0216502222',
    tollFree: '0806502222',
    escortInfo: 'CPS operates 24/7 from seven service centres across UCT campuses.',
    offCampusNote: 'Off-campus residences (e.g. Obz Square surrounds) may have limited CPS coverage.',
    sourceUrl: 'https://www.uct.ac.za/staff/campus-protection-services',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of Fort Hare',
    protectionLabel: 'UFH Campus Security',
    protectionServices: '0406022243',
    lines: [
      { label: 'Alice campus', number: '0406022243' },
      { label: 'East London campus', number: '0437047396' },
    ],
    offCampusNote: 'Off-campus student housing is outside UFH security coverage.',
    sourceUrl: 'https://www.ufh.ac.za/emergency-contact',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of Johannesburg',
    protectionLabel: 'UJ Protection Services',
    protectionServices: '0115592555',
    lines: [
      { label: 'APK (Kingsway)', number: '0115592555' },
      { label: 'APB (Bunting)', number: '0115591312' },
      { label: 'DFC (Doornfontein)', number: '0115596450' },
      { label: 'SWC (Soweto)', number: '0115595555' },
    ],
    escortInfo: 'Campus security escorts and medical emergency response available.',
    offCampusNote: 'Off-campus student housing may be outside UJ security patrol areas.',
    sourceUrl: 'https://www.uj.ac.za/support-services/operations/protection-services/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of KwaZulu-Natal',
    protectionLabel: 'UKZN Risk Management Services',
    protectionServices: '0312603777',
    lines: [
      { label: 'Howard College', number: '0312603777' },
      { label: 'Edgewood', number: '0312603474' },
      { label: 'Pietermaritzburg', number: '0332605211' },
      { label: 'Westville', number: '0312607133' },
      { label: 'Medical School', number: '0312604239' },
    ],
    offCampusNote: 'Off-campus residences are outside UKZN RMS coverage.',
    sourceUrl: 'https://rms.ukzn.ac.za/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of Limpopo',
    protectionLabel: 'UL Campus Protection Services',
    protectionServices: '0152686123',
    lines: [
      { label: 'After hours', number: '0152686123' },
      { label: 'Control (office hours)', number: '0152682823' },
    ],
    offCampusNote: 'Off-campus students face heightened risk — UL security covers campus precincts.',
    sourceUrl: 'https://www.ul.ac.za/student-counselling/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of Mpumalanga',
    protectionLabel: 'UMP switchboard',
    protectionServices: '0130020001',
    lines: [
      { label: 'Mbombela', number: '0130020001' },
      { label: 'Siyabuswa', number: '0130020800' },
    ],
    noDedicatedLineNote:
      'UMP does not publish a dedicated security hotline — call switchboard and ask for campus security.',
    offCampusNote: 'Off-campus housing is outside UMP security coverage.',
    sourceUrl: 'https://www.ump.ac.za/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of Pretoria',
    protectionLabel: 'UP Operational Management Centre',
    protectionServices: '0124202310',
    tollFree: '0800006428',
    lines: [{ label: 'OMC (alt)', number: '0124202760' }],
    escortInfo: '24-hour OMC handles security and medical emergencies on all UP campuses.',
    offCampusNote: 'Off-campus bookings rely on SAPS — UP security covers campus precincts.',
    sourceUrl: 'https://www.up.ac.za/department-of-security-services/emergency-numbers',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of South Africa',
    protectionLabel: 'Unisa Protection Services',
    protectionServices: '0124296666',
    escortInfo:
      '24/7 control office at Muckleneuk — reachable from any Unisa campus or regional office.',
    offCampusNote:
      'Unisa is primarily distance learning; regional offices have local security arrangements.',
    sourceUrl: 'https://www.unisa.ac.za/sites/myunisa/default/Announcements/How-to-keep-safe-at-Unisa-campuses-and-regional-offices',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of the Free State',
    protectionLabel: 'UFS Protection Services',
    protectionServices: '0514012911',
    tollFree: '0800204682',
    lines: [
      { label: 'Bloemfontein', number: '0514012911' },
      { label: 'Qwaqwa', number: '0587185460' },
      { label: 'South campus (Bloemfontein)', number: '0515051217' },
    ],
    escortInfo: '24/7 operational centre on all UFS campuses.',
    offCampusNote: 'Off-campus residences are outside UFS security coverage.',
    sourceUrl: 'https://www.ufs.ac.za/supportservices/departments/protection-services-home/resources/frequently-asked-questions',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of the Western Cape',
    protectionLabel: 'UWC Campus Protection Services',
    protectionServices: '0219592100',
    escortInfo: '24-hour emergency line — state your name, location, and nature of crisis.',
    offCampusNote: 'Off-campus student housing is outside UWC CPS coverage.',
    sourceUrl: 'https://www.uwc.ac.za/about/finance-and-operations/risk-and-compliance',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of the Witwatersrand',
    protectionLabel: 'Wits Protection Services',
    protectionServices: '0117174444',
    lines: [
      { label: 'East campus', number: '0117174444' },
      { label: 'East campus (alt)', number: '0117176666' },
      { label: 'West campus', number: '0117171842' },
      { label: 'Health Sciences', number: '0117172222' },
      { label: 'Education campus', number: '0117173340' },
    ],
    escortInfo: '24-hour campus escort — call Protection Services.',
    safetyApp: 'mySOS',
    safetyAppNote: 'Download mySOS and use the Wits button on campus.',
    offCampusNote:
      'Private residences (e.g. Braamfontein, Junction) are outside Wits Protection coverage.',
    sourceUrl: 'https://www.wits.ac.za/people/emergency-contacts/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of Venda',
    protectionLabel: 'Univen Protection Services',
    protectionServices: '0159628193',
    lines: [
      { label: 'Control room', number: '0159628193' },
      { label: 'Main gate', number: '0159628120' },
      { label: 'CCTV control room', number: '0159628603' },
    ],
    escortInfo: '24-hour escort service on campus — call Protection Services.',
    offCampusNote: 'Off-campus housing in Thohoyandou is outside Univen security coverage.',
    sourceUrl: 'https://www.univen.ac.za/safety-security-campus/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'University of Zululand',
    protectionLabel: 'Unizulu Protective Services',
    protectionServices: '0359026000',
    lines: [
      { label: 'KwaDlangezwa switchboard', number: '0359026000' },
      { label: 'Richards Bay switchboard', number: '0359026950' },
    ],
    noDedicatedLineNote:
      'Unizulu does not publish a dedicated emergency security line — call switchboard and ask for Protective Services.',
    offCampusNote: 'Off-campus residences are outside PSD patrol areas.',
    sourceUrl: 'https://www.unizulu.ac.za/contact-us/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Vaal University of Technology',
    protectionLabel: 'VUT switchboard',
    protectionServices: '0169509000',
    noDedicatedLineNote:
      'VUT does not publish a dedicated campus security hotline — call switchboard and ask for security assistance.',
    offCampusNote: 'Off-campus housing in Vanderbijlpark is outside VUT security coverage.',
    sourceUrl: 'https://www.vut.ac.za/contact-us/',
    lastVerified: '2026-07-25',
  },
  {
    institution: 'Walter Sisulu University',
    protectionLabel: 'WSU Campus Security',
    protectionServices: '0475022100',
    lines: [
      { label: 'Mthatha', number: '0475022100' },
      { label: 'Butterworth', number: '0474016000' },
      { label: 'East London', number: '0437029200' },
      { label: 'Potsdam', number: '0437085200' },
      { label: 'Chiselhurst', number: '0437094000' },
    ],
    offCampusNote: 'Off-campus residences are outside WSU security coverage.',
    sourceUrl: 'https://www.wsu.ac.za/index.php/en/occupational-health-and-safety',
    lastVerified: '2026-07-25',
  },
];

export function getCampusSafetyContact(
  institution: string | null | undefined,
): CampusSafetyContact | null {
  if (!institution?.trim()) return null;
  const norm = institution.trim().toLowerCase();
  return (
    CAMPUS_SAFETY_CONTACTS.find((c) => c.institution.toLowerCase() === norm) ??
    CAMPUS_SAFETY_CONTACTS.find((c) => norm.includes(c.institution.toLowerCase())) ??
    CAMPUS_SAFETY_CONTACTS.find((c) => c.institution.toLowerCase().includes(norm)) ??
    null
  );
}

export interface SharedSpot {
  label: string;
  notes?: string;
}

/** Suggested public meet-up spots per institution (v1 static list). */
export const CAMPUS_SHARED_SPOTS: Readonly<Record<string, readonly SharedSpot[]>> = {
  'University of the Witwatersrand': [
    { label: 'Wits Library entrance (Braamfontein)', notes: 'Busy, well-lit, security nearby.' },
    { label: 'Matrix / Student Centre', notes: 'Central campus hub.' },
    { label: 'Residence common room (by arrangement)', notes: 'Use your res lobby, not a private room.' },
  ],
  'University of Johannesburg': [
    { label: 'APK Library foyer', notes: 'High foot traffic during term.' },
    { label: 'Student Centre (APK)', notes: 'Security presence on campus.' },
  ],
  'University of Pretoria': [
    { label: 'Hatfield Campus Student Centre', notes: 'Central, public area.' },
    { label: 'Merensky Library entrance', notes: 'Well-lit study hub.' },
  ],
};

export function getSharedSpots(institution: string | null | undefined): readonly SharedSpot[] {
  if (!institution) return [];
  const direct = CAMPUS_SHARED_SPOTS[institution];
  if (direct) return direct;
  const key = Object.keys(CAMPUS_SHARED_SPOTS).find((k) =>
    institution.toLowerCase().includes(k.toLowerCase()),
  );
  return key ? CAMPUS_SHARED_SPOTS[key]! : [];
}
